// oxlint-disable max-lines-per-function, complexity
import { merge } from 'lodash-es';
import { assertEvent, enqueueActions } from 'xstate';

import { machine } from './actor';

// this will be used to provide the implementation with external dependencies
export const createLogic = () =>
	machine.provide({
		actions: {
			onSubmit: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'submit');

				const updatedDraft = merge({}, context.draft, event.draft);

				// update the child machine's context
				enqueue.assign({ draft: updatedDraft });

				// sync the updated child machine's context to the parent machine's context
				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),
			onLoadTimeout: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'error');

				// mirror the routing guard: with a seeded issue we continue the AI flow (→ personal-story),
				// otherwise we drop to the flow chooser (→ start) with the type cleared.
				// input.flow must also be cleared here — otherwise it stays 'chat' after this manual
				// fallback, and preview's `back` guard (isChatFlow) misroutes to the chat intro instead
				// of personal-story once the user finishes the manual steps.
				const hasIssue = Boolean(context.draft?.input?.issuePrompt);
				const updatedDraft = merge({}, context.draft, {
					type: hasIssue ? 'ai_draft' : null,
					input: { flow: null },
				});

				enqueue.assign({ draft: updatedDraft });

				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),
			onError: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'error');

				const currentDraft = context.draft;

				const { issuePrompt = '', personalStory = '' } = currentDraft?.input ?? {};
				const description = `${issuePrompt} <br /><br />${personalStory}`;

				const userDraft = {
					type: 'user_draft',
					petitionDraft: { ask: null, description, image: null },
					recommendedDraft: null,
					editingDraft: null,
					metadata: { imageJobId: null },
				};

				const updatedDraft = merge({}, context.draft, userDraft);

				enqueue.assign({ draft: updatedDraft });

				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),
			unsetDraftType: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'back');

				const targetBranch = event.target; // target = null

				const updatedDraft = (() => {
					if (targetBranch === null) return merge({}, context.draft, { type: null });

					return context.draft;
				})();

				enqueue.assign({ draft: updatedDraft });

				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),
			persistDraftType: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'submit');

				const baseDraft = merge({}, context.draft, event.draft);

				const targetBranch = event.target; // 'ai' | 'user'

				const updatedDraft = (() => {
					if (targetBranch === null) return merge({}, baseDraft, { type: null });
					if (targetBranch === 'ai') return merge({}, baseDraft, { type: 'ai_draft' });
					if (targetBranch === 'user') return merge({}, baseDraft, { type: 'user_draft' });
					return baseDraft;
				})();

				enqueue.assign({ draft: updatedDraft });

				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),
		},
		guards: {
			transitionTo: ({ event }, params) => {
				assertEvent(event, 'submit');

				return event.target === params.target;
			},
			// -- branching and transition point
			isUserDraft: ({ context, event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'user';
				}
				return context.draft?.type === 'user_draft';
			},
			isAiDraft: ({ context, event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'ai';
				}
				return context.draft?.type === 'ai_draft';
			},
			didOptOut: ({ event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'start';
				}
				return false;
			},
			isChatFlow: ({ context }) => context.draft?.input?.flow === 'chat',
			isLoadTimeout: ({ event }) => event.type === 'error' && event.reason === 'load_timeout',
			isLoadTimeoutWithIssue: ({ context, event }) =>
				event.type === 'error' && event.reason === 'load_timeout' && Boolean(context.draft?.input?.issuePrompt),
		},
	});
